/**
 * Персоны интерфейса — ПРОЕКЦИЯ ролей канона, а не собственный словарь прав.
 *
 * В прототипе «вход» был выбором персоны из трёх карточек, и права означали
 * ровно две вещи: какие разделы видны и от чьего имени пишутся мок-действия.
 * Здесь роль приходит от сервера (`/auth/me`), и соответствие однозначное
 * (решение 7 плана `astra-on-canon`):
 *
 * * `hokimiyat_manager` → «Хоким», дом — кабинет решений;
 * * `verifier` → «Диспетчер», дом — очередь проверки;
 * * `q_inspector` → «Инспектор карантина», `q_officer` → «Ответственный
 *   карантинной службы» (волна 2б): интерфейс района и рабочее место ввода;
 * * `farm` → «Фермер», дом — «что от меня ждут сегодня»;
 * * `observer` и аккаунт без роли → «Наблюдатель»: интерфейс хокима без
 *   действий.
 *
 * Персона по-прежнему отвечает только на вопрос «что ПОКАЗАТЬ». Что РАЗРЕШЕНО
 * — решает сервер, и спрятанная кнопка ничего не защищает.
 */

import { Bug, Building2, ClipboardCheck, Eye, Gavel, Landmark, type LucideIcon } from 'lucide-react';
import { defineMessages } from '../../i18n/define';
import { tr } from '../../i18n/runtime';

const m = defineMessages({
  hokimTitle: {
    id: 'auth.persona.hokim.title',
    defaultMessage: 'Хоким',
    description: 'Название роли (хоким — глава района) на экране входа и в подвале меню; коротко',
  },
  hokimTagline: {
    id: 'auth.persona.hokim.tagline',
    defaultMessage: 'Просмотр и решения по району',
    description: 'Подзаголовок карточки роли «Хоким» на экране входа: чей это взгляд',
  },
  hokimSees1: {
    id: 'auth.persona.hokim.sees1',
    defaultMessage: 'Обзор района, агроциклы, карта, хозяйства, реестр контуров',
    description: 'Пункт списка «Что видит роль» на карточке роли хоким (экран входа); одна строка',
  },
  hokimSees2: {
    id: 'auth.persona.hokim.sees2',
    defaultMessage: 'Отчитались — основная ось; отметки проверяющего видны рядом, без доли',
    description: 'Пункт списка «Что видит роль» на карточке роли хоким (экран входа); одна строка',
  },
  hokimSees3: {
    id: 'auth.persona.hokim.sees3',
    defaultMessage: 'Очередь внимания и происхождение каждой цифры',
    description: 'Пункт списка «Что видит роль» на карточке роли хоким (экран входа); одна строка',
  },
  hokimSees4: {
    id: 'auth.persona.hokim.sees4',
    defaultMessage: 'Поручения и кампании района',
    description: 'Пункт списка «Что видит роль» на карточке роли хоким (экран входа); одна строка',
  },
  hokimScope: {
    id: 'auth.persona.hokim.scope',
    defaultMessage: 'Уровень района: 22 массива, все хозяйства и контуры среза',
    description: 'Уровень просмотра роли «Хоким» на карточке роли (экран входа); одна строка',
  },
  managerTitle: {
    id: 'auth.persona.manager.title',
    defaultMessage: 'Диспетчер управления',
    description: 'Название роли (сотрудник управления, проверяющий отчёты) на экране входа и в подвале меню',
  },
  managerTagline: {
    id: 'auth.persona.manager.tagline',
    defaultMessage: 'Проверка отчётов и ввод за хозяйство',
    description: 'Подзаголовок карточки роли «Диспетчер управления» на экране входа',
  },
  managerSees1: {
    id: 'auth.persona.manager.sees1',
    defaultMessage: 'Всё, что видит хоким, — станция целиком',
    description: 'Пункт списка «Что видит роль» на карточке роли диспетчер управления (экран входа); одна строка',
  },
  managerSees2: {
    id: 'auth.persona.manager.sees2',
    defaultMessage: 'Очередь проверки: подтвердить или опровергнуть с причиной',
    description: 'Пункт списка «Что видит роль» на карточке роли диспетчер управления (экран входа); одна строка',
  },
  managerSees3: {
    id: 'auth.persona.manager.sees3',
    defaultMessage: 'Ввод за хозяйство с обязательным авторством',
    description: 'Пункт списка «Что видит роль» на карточке роли диспетчер управления (экран входа); одна строка',
  },
  managerSees4: {
    id: 'auth.persona.manager.sees4',
    defaultMessage: 'Запускает кампании работ и переносит их сроки',
    description: 'Пункт списка «Что видит роль» на карточке роли диспетчер управления (экран входа); одна строка',
  },
  managerScope: {
    id: 'auth.persona.manager.scope',
    defaultMessage: 'Уровень района плюс раздел «Управление»',
    description: 'Уровень просмотра роли «Диспетчер управления» на карточке роли (экран входа); одна строка',
  },
  observerTitle: {
    id: 'auth.persona.observer.title',
    defaultMessage: 'Наблюдатель',
    description: 'Название роли только для чтения на экране входа и в подвале меню; коротко',
  },
  observerTagline: {
    id: 'auth.persona.observer.tagline',
    defaultMessage: 'Чтение района',
    description: 'Подзаголовок роли «Наблюдатель»: чей это взгляд',
  },
  observerSees1: {
    id: 'auth.persona.observer.sees1',
    defaultMessage: 'Те же экраны, что видит хоким',
    description: 'Пункт списка «Что видит роль» на карточке роли наблюдатель (экран входа); одна строка',
  },
  observerSees2: {
    id: 'auth.persona.observer.sees2',
    defaultMessage: 'Отчитались — основная ось; отметки проверяющего видны рядом, без доли',
    description: 'Пункт списка «Что видит роль» на карточке роли наблюдатель (экран входа); одна строка',
  },
  observerSees3: {
    id: 'auth.persona.observer.sees3',
    defaultMessage: 'Только чтение: записи сервер не примет, кнопок записи на экранах нет',
    description: 'Пункт списка «Что видит роль» на карточке роли наблюдатель (экран входа); одна строка',
  },
  observerScope: {
    id: 'auth.persona.observer.scope',
    defaultMessage: 'Уровень района, только чтение',
    description: 'Уровень просмотра роли «Наблюдатель» на карточке роли (экран входа); одна строка',
  },
  inspectorTitle: {
    id: 'auth.persona.inspector.title',
    defaultMessage: 'Инспектор карантина',
    description: 'Название роли инспектора карантинной службы на экране входа и в подвале меню',
  },
  inspectorTagline: {
    id: 'auth.persona.inspector.tagline',
    defaultMessage: 'Обследования, находки, замеры',
    description: 'Подзаголовок роли «Инспектор карантина»: чей это взгляд',
  },
  inspectorSees1: {
    id: 'auth.persona.inspector.sees1',
    defaultMessage: 'Экран «Карантин» и карточки контуров — как у хокима',
    description: 'Пункт списка «Что видит роль» на карточке роли инспектор карантина (экран входа); одна строка',
  },
  inspectorSees2: {
    id: 'auth.persona.inspector.sees2',
    defaultMessage: 'Ввод: обследование с находкой (вид, часть, площадь), предписание, замер',
    description: 'Пункт списка «Что видит роль» на карточке роли инспектор карантина (экран входа); одна строка',
  },
  inspectorSees3: {
    id: 'auth.persona.inspector.sees3',
    defaultMessage: 'Устранено — только по вашему замеру; красный и снятие красного — у ответственного',
    description: 'Пункт списка «Что видит роль» на карточке роли инспектор карантина (экран входа); одна строка',
  },
  inspectorScope: {
    id: 'auth.persona.inspector.scope',
    defaultMessage: 'Уровень района; запись принимает сервер по роли',
    description: 'Уровень просмотра роли «Инспектор карантина» на карточке роли (экран входа); одна строка',
  },
  officerTitle: {
    id: 'auth.persona.officer.title',
    defaultMessage: 'Ответственный карантинной службы',
    description: 'Название роли ответственного карантинной службы (решает по красным случаям) на экране входа и в подвале меню',
  },
  officerTagline: {
    id: 'auth.persona.officer.tagline',
    defaultMessage: 'Решения по карантинным случаям',
    description: 'Подзаголовок роли «Ответственный карантинной службы»: чей это взгляд',
  },
  officerSees1: {
    id: 'auth.persona.officer.sees1',
    defaultMessage: 'Экран «Карантин» и карточки контуров — как у хокима',
    description: 'Пункт списка «Что видит роль» на карточке роли ответственный карантинной службы (экран входа); одна строка',
  },
  officerSees2: {
    id: 'auth.persona.officer.sees2',
    defaultMessage: 'Очередь «требует решения»: красный, без красного, снятие красного',
    description: 'Пункт списка «Что видит роль» на карточке роли ответственный карантинной службы (экран входа); одна строка',
  },
  officerSees3: {
    id: 'auth.persona.officer.sees3',
    defaultMessage: 'Направление в агроинспекцию — по красной единице земли',
    description: 'Пункт списка «Что видит роль» на карточке роли ответственный карантинной службы (экран входа); одна строка',
  },
  officerScope: {
    id: 'auth.persona.officer.scope',
    defaultMessage: 'Уровень района; запись принимает сервер по роли',
    description: 'Уровень просмотра роли «Ответственный карантинной службы» на карточке роли (экран входа); одна строка',
  },
  farmerTitle: {
    id: 'auth.persona.farmer.title',
    defaultMessage: 'Фермер',
    description: 'Название роли фермера на экране входа и в подвале меню; коротко',
  },
  farmerTagline: {
    id: 'auth.persona.farmer.tagline',
    defaultMessage: 'Своё хозяйство: что сегодня и чем отчитаться',
    description: 'Подзаголовок карточки роли «Фермер»: чей это взгляд',
  },
  farmerSees1: {
    id: 'auth.persona.farmer.sees1',
    defaultMessage: 'Сегодня: обязательства своего хозяйства в открытых окнах',
    description: 'Пункт списка «Что видит роль» на карточке роли фермер (экран входа); одна строка',
  },
  farmerSees2: {
    id: 'auth.persona.farmer.sees2',
    defaultMessage: 'Мои поля: свои контуры и что на них размещено',
    description: 'Пункт списка «Что видит роль» на карточке роли фермер (экран входа); одна строка',
  },
  farmerSees3: {
    id: 'auth.persona.farmer.sees3',
    defaultMessage: 'Отчёты: что отправлено и что с этим сделала проверка',
    description: 'Пункт списка «Что видит роль» на карточке роли фермер (экран входа); одна строка',
  },
  farmerScope: {
    id: 'auth.persona.farmer.scope',
    defaultMessage: 'Уровень хозяйства: станция района фермеру не показывается',
    description: 'Уровень просмотра роли «Фермер» на карточке роли (экран входа); одна строка',
  },
  guest: {
    id: 'auth.persona.guest',
    defaultMessage: 'Гость',
    description: 'Название персоны без входа (в чипе и подвале меню); коротко',
  },
  farmFallback: {
    id: 'auth.persona.farmFallback',
    defaultMessage: 'Хозяйство',
    description: 'Запасное название персоны фермера, когда имя хозяйства неизвестно; коротко',
  },
  farmNo: {
    id: 'auth.persona.farmNo',
    defaultMessage: 'Хозяйство #{id}',
    description: 'Название хозяйства фермера, когда сервер имя не назвал; {id} — номер хозяйства',
  },
});


export type Role = 'hokim' | 'manager' | 'farmer' | 'observer' | 'inspector' | 'officer';

export interface Persona {
  role: Role;
  /** Только у фермера: хозяйство, от имени которого он смотрит. */
  farmId?: string;
  farmName?: string;
  /**
   * Смотрит БЕЗ входа.
   *
   * Чтение района на сервере открыто без сессии (`backend/serving/scope.py`:
   * область анонима — `district`, пока `AGROAI_READ_REQUIRES_LOGIN`
   * выключена), поэтому аноним получает интерфейс наблюдателя, а не экран
   * входа: закрывать на клиенте то, что сервер отдаёт, значило бы разойтись
   * с ним в том, кому открыто чтение. Признак читается — им оболочка
   * заменяет «Выйти» на «Войти» (`shell/Sidebar.tsx`, `shell/Topbar.tsx`),
   * поэтому он не «право, которое никто не проверяет».
   */
  anonymous?: boolean;
}

/*
 * Признака `readOnly` здесь НЕТ намеренно.
 *
 * Спека требует от наблюдателя «интерфейс без действий», и до этого среза
 * персона наблюдателя несла поле `readOnly: true`. Ни один экран его не читал:
 * наблюдатель видел то же меню и те же кнопки, что хоким. Поле, которое
 * выглядит правом, но ничего не запрещает, хуже его отсутствия — читающий код
 * считает правило выполненным и не ищет, где оно принуждается.
 *
 * Требование остаётся открытым и закрывается задачей 1.7 плана
 * `astra-on-canon` вместе с остальными правами: там действия записи получают
 * канонический источник, и «нет права» станет отказом сервера, а не
 * спрятанной кнопкой. Тогда же признак возвращается — вместе с тестом «у
 * наблюдателя на экране нет действий записи».
 */

/**
 * Роль канона → персона интерфейса.
 *
 * Порядок разбора не косметический. `hokimiyat_manager` проверяется раньше
 * `verifier`, потому что у аккаунта хокимията обе роли бывают сразу, а его
 * работа начинается с решений, а не с очереди. Аккаунт хозяйства не может
 * оказаться диспетчером района: у него другая область.
 */
export function roleFromCanon(roles: readonly string[]): Role {
  if (roles.includes('hokimiyat_manager')) return 'hokim';
  if (roles.includes('verifier')) return 'manager';
  if (roles.includes('q_officer')) return 'officer';
  if (roles.includes('q_inspector')) return 'inspector';
  if (roles.includes('farm')) return 'farmer';
  return 'observer';
}

export interface RoleSpec {
  role: Role;
  /** Имя роли в интерфейсе. */
  title: string;
  /** Одна строка: чей это взгляд. */
  tagline: string;
  /** Что роль видит — по пунктам, без обещаний, которых прототип не держит. */
  sees: string[];
  /** Уровень просмотра по docs/rules/screen-levels.md. */
  scope: string;
  icon: LucideIcon;
  /** Куда роль попадает сразу после входа. */
  home: string;
  /** Нужен ли второй шаг — выбор хозяйства. */
  needsFarm: boolean;
}

export const ROLE_SPECS: Record<Role, RoleSpec> = {
  hokim: {
    role: 'hokim',
    get title() {
      return tr(m.hokimTitle);
    },
    get tagline() {
      return tr(m.hokimTagline);
    },
    get sees() {
      return [tr(m.hokimSees1), tr(m.hokimSees2), tr(m.hokimSees3), tr(m.hokimSees4)];
    },
    get scope() {
      return tr(m.hokimScope);
    },
    icon: Landmark,
    home: '/',
    needsFarm: false,
  },
  manager: {
    role: 'manager',
    get title() {
      return tr(m.managerTitle);
    },
    get tagline() {
      return tr(m.managerTagline);
    },
    get sees() {
      return [tr(m.managerSees1), tr(m.managerSees2), tr(m.managerSees3), tr(m.managerSees4)];
    },
    get scope() {
      return tr(m.managerScope);
    },
    icon: ClipboardCheck,
    // Дом диспетчера — его рабочее место, а не витрина района: очередь
    // проверки (решение 7). Картина сезона остаётся доступной по «/».
    home: '/ops/verify',
    needsFarm: false,
  },
  observer: {
    role: 'observer',
    get title() {
      return tr(m.observerTitle);
    },
    get tagline() {
      return tr(m.observerTagline);
    },
    get sees() {
      return [tr(m.observerSees1), tr(m.observerSees2), tr(m.observerSees3)];
    },
    get scope() {
      return tr(m.observerScope);
    },
    icon: Eye,
    home: '/',
    needsFarm: false,
  },
  inspector: {
    role: 'inspector',
    get title() {
      return tr(m.inspectorTitle);
    },
    get tagline() {
      return tr(m.inspectorTagline);
    },
    get sees() {
      return [tr(m.inspectorSees1), tr(m.inspectorSees2), tr(m.inspectorSees3)];
    },
    get scope() {
      return tr(m.inspectorScope);
    },
    icon: Bug,
    home: '/ops/quarantine',
    needsFarm: false,
  },
  officer: {
    role: 'officer',
    get title() {
      return tr(m.officerTitle);
    },
    get tagline() {
      return tr(m.officerTagline);
    },
    get sees() {
      return [tr(m.officerSees1), tr(m.officerSees2), tr(m.officerSees3)];
    },
    get scope() {
      return tr(m.officerScope);
    },
    icon: Gavel,
    home: '/ops/quarantine',
    needsFarm: false,
  },
  farmer: {
    role: 'farmer',
    get title() {
      return tr(m.farmerTitle);
    },
    get tagline() {
      return tr(m.farmerTagline);
    },
    get sees() {
      return [tr(m.farmerSees1), tr(m.farmerSees2), tr(m.farmerSees3)];
    },
    get scope() {
      return tr(m.farmerScope);
    },
    icon: Building2,
    home: '/my/today',
    needsFarm: true,
  },
};

/**
 * Порядок карточек-описаний на экране входа. Наблюдателя там нет: в него не
 * входят намеренно — им становится аккаунт, которому роль ещё не выдана.
 */
export const ROLE_ORDER: Role[] = ['hokim', 'manager', 'farmer'];

export function roleSpec(role: Role): RoleSpec {
  return ROLE_SPECS[role];
}

/* ------------------------------------------------------ своё хозяйство */

/**
 * Хозяйство фермера — из `/auth/me`, а не из выбора на клиенте.
 *
 * Прежде вход просил выбрать «демонстрационное» хозяйство из реестра фикстур:
 * кабинет стоял на файлах прототипа, и связать аккаунт с их строковыми
 * идентификаторами было нечем. Кабинет переведён на `/my/works` (задача 1.7),
 * и выбор чужого хозяйства стал ошибкой, а не демонстрацией: сервер отвечает
 * отказом на чужой идентификатор, и кабинет открывался пустым. Теперь
 * хозяйство — это `principal.actors` канона (решение 7 плана
 * `astra-on-canon`).
 *
 * Локально хранится одно: КАКОЕ из своих хозяйств открыто, если аккаунт
 * связан с несколькими. Запись, которая не называет своё хозяйство аккаунта
 * (след прежнего выбора из фикстур), не читается вовсе.
 */
export const OWN_FARM_KEY = 'astra-own-farm';

export interface OwnFarm {
  farmId: string;
  farmName: string;
}

/** Хозяйство аккаунта в словаре персоны: ключ канона строкой. */
export function ownFarms(actors: readonly { id: number; name?: string | null }[]): OwnFarm[] {
  return actors.map((actor) => ({
    farmId: String(actor.id),
    farmName: actor.name ?? tr(m.farmNo, { id: actor.id }),
  }));
}

function readChosen(): string | null {
  try {
    return window.localStorage.getItem(OWN_FARM_KEY);
  } catch {
    return null;
  }
}

/**
 * Какое хозяйство открыто. Одно своё — оно и открыто, без вопроса. Несколько —
 * то, что выбрано из СВОИХ; `null` — выбора ещё нет, и вход спросит.
 */
export function pickOwnFarm(farms: readonly OwnFarm[]): OwnFarm | null {
  if (farms.length === 0) return null;
  if (farms.length === 1) return farms[0]!;
  const chosen = readChosen();
  return farms.find((farm) => farm.farmId === chosen) ?? null;
}

export function writeOwnFarm(farmId: string): void {
  try {
    window.localStorage.setItem(OWN_FARM_KEY, farmId);
  } catch {
    // Приватный режим браузера: выбор просто не переживёт перезагрузку.
  }
}

export function dropOwnFarm(): void {
  try {
    window.localStorage.removeItem(OWN_FARM_KEY);
    // След прежнего выбора хозяйства фикстур: читать его больше некому.
    window.localStorage.removeItem('astra-demo-farm');
  } catch {
    // См. выше: писать некуда — и читать будет нечего.
  }
}

/* ------------------------------------------------------------------ доступ */

/**
 * Маршруты станции, которые фермер всё-таки видит: конкретный контур и
 * конкретная работа — это его собственный контекст, а не витрина района.
 * Списочные экраны района (`/contours`, `/farms`, `/cycles`) сюда не входят.
 *
 * Здесь проверяется ТОЛЬКО форма адреса: чей это контур и чья работа, шаблон
 * пути не знает. Границу по данным держат сами экраны — `astra/useAnalysis`
 * навязывает фермеру `farmId` его персоны, а `pages/ContourDetail` отказывает
 * в паспорте чужого контура словами.
 */
const FARMER_EXTRA: RegExp[] = [
  /^\/contours\/[^/]+$/,
  /^\/cycles\/[^/]+\/works\/[^/]+$/,
];

/** Нормализация пути: хвостовой слэш не должен решать вопрос доступа. */
function tidy(pathname: string): string {
  return pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
}

/**
 * Видна ли роли эта страница. Хоким и диспетчер видят всё; фермеру станция
 * закрыта — его уровень заканчивается своим хозяйством.
 *
 * Это проверка МАРШРУТА, а не данных: она отвечает «страницу открывать можно»,
 * и ровно поэтому две страницы из `FARMER_EXTRA` сами сужают срез до хозяйства
 * персоны. Ни одна из двух проверок не заменяет другую.
 */
export function isRouteAllowed(role: Role, pathname: string): boolean {
  if (role !== 'farmer') return true;
  const path = tidy(pathname);
  if (path === '/my' || path.startsWith('/my/')) return true;
  return FARMER_EXTRA.some((re) => re.test(path));
}

export function homeFor(role: Role): string {
  return ROLE_SPECS[role].home;
}

/** Как персона называется в чипе и в подвале сайдбара. */
export function personaLabel(persona: Persona): string {
  if (persona.anonymous) return tr(m.guest);
  if (persona.role === 'farmer') return persona.farmName ?? persona.farmId ?? tr(m.farmFallback);
  return ROLE_SPECS[persona.role].title;
}
